import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, DialogActions } from "@mui/material";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import DeveloperBuilding from "./DeveloperBuilding";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import CommentInputForReason from "Components/Common/CommentInputForReason";
import {
  applicationApprovalStatusTypes,
  FIRE_EMERGENCY_SR_MANAGER,
} from "common/constant";

import { isAuthorizedUser } from "utils";
import { getAllowedStatusForFireSafetyApplications } from "utils/getAllowedStatusForFireSafetyApplications";

import { usePutUpdateFireSafetyApplicationStatusMutation } from "apis/AuditSchedular";

const FireSafetyBuildingApprovalActionButton = ({
  isBuildingNameSelected,
  applicationData,
  handleClose,
  t,
}) => {
  const [updateStatus, { isLoading: statusLoading }] =
    usePutUpdateFireSafetyApplicationStatusMutation();

  const [isRejectionCommentModalVisible, setIsRejectionCommentModalVisible] =
    useState(false);
  const [isRevokedCommentModalVisible, setIsRevokedCommentModalVisible] =
    useState(false);
  const [isApproveAndAuditButtonClicked, setIsApproveAndAuditButtonClicked] =
    useState(false);
  const [isApproveBtnClicked, setIsApproveBtnClicked] = useState(false);

  const { approvedBy, revokedBy, rejectedBy, id } = applicationData;

  const { roles } = useSelector((state) => state.Login);

  const { canApproved, canRejected, canRevoked } =
    getAllowedStatusForFireSafetyApplications(
      {
        approvedBy,
        rejectedBy,
        revokedBy,
      },
      roles
    );

  const isFireSrManager = useMemo(
    () => isAuthorizedUser(roles, [FIRE_EMERGENCY_SR_MANAGER]),
    [roles]
  );

  const submitHandlerForRejection = async (comment) => {
    try {
      await updateStatus({
        buildingInformationId: id,
        status: applicationApprovalStatusTypes["Rejected"],
        comment,
      });
      handleClose();
    } catch (err) {}
  };

  const submitHandlerForApprove = async () => {
    try {
      await updateStatus({
        buildingInformationId: id,
        status: applicationApprovalStatusTypes["Approved"],
      });
      handleClose();
    } catch (err) {}
  };

  const submitHandlerForRevoked = async (comment) => {
    try {
      await updateStatus({
        buildingInformationId: id,
        status: applicationApprovalStatusTypes["Revoked"],
        comment,
      });
      handleClose();
      setIsRevokedCommentModalVisible(false);
    } catch (err) {}
  };

  const handleCloseForApprove = (isSubmitting = false) => {
    setIsApproveAndAuditButtonClicked(false);
    if (isSubmitting) {
      handleClose();
    }
  };

  return (
    <>
      {(isApproveBtnClicked || isApproveAndAuditButtonClicked) && (
        <>
          {isApproveAndAuditButtonClicked && isFireSrManager ? (
            <DeveloperBuilding
              applicationId={id}
              handleClose={handleCloseForApprove}
              buildingName={applicationData?.developerBuilding?.name ?? ""}
            />
          ) : (
            <ConfirmationDialog
              message="Are you sure you want to approve application?"
              onCancel={() => {
                setIsApproveAndAuditButtonClicked(false);
                setIsApproveBtnClicked(false);
              }}
              onConfirm={submitHandlerForApprove}
              isOpen={isApproveAndAuditButtonClicked || isApproveBtnClicked}
              title="Warning"
              variant="warning"
              isLoading={statusLoading}
            />
          )}
        </>
      )}
      <CommentInputForReason
        handleClose={() => {
          setIsRejectionCommentModalVisible(false);
        }}
        handleSubmit={submitHandlerForRejection}
        isOpen={isRejectionCommentModalVisible}
        isLoading={statusLoading && isRejectionCommentModalVisible}
        title="Add Reason for Rejection"
        errorMessage="Please enter reason for rejection"
      />
      <CommentInputForReason
        handleClose={() => {
          setIsRevokedCommentModalVisible(false);
        }}
        handleSubmit={submitHandlerForRevoked}
        isOpen={isRevokedCommentModalVisible}
        isLoading={statusLoading && isRevokedCommentModalVisible}
        title="Add Reason for Revoked"
        errorMessage="Please enter reason for revoked"
      />

      <DialogActions className="border-top">
        {!isBuildingNameSelected && isFireSrManager && canApproved && (
          <Button
            variant="contained"
            color="success"
            sx={{
              backgroundColor: "#ff9900",
              "&:hover": {
                backgroundColor: "#ff9900",
              },
            }}
            onClick={() => setIsApproveBtnClicked(true)}
          >
            {t("Approve")}
          </Button>
        )}

        {canApproved && (
          <Button
            variant="contained"
            color="success"
            onClick={() => setIsApproveAndAuditButtonClicked(true)}
          >
            {t(isFireSrManager ? "Approve & Audit" : "Approve")}
          </Button>
        )}
        {canRejected && (
          <Button
            variant="contained"
            color="error"
            onClick={() => setIsRejectionCommentModalVisible(true)}
          >
            {t("Reject")}
          </Button>
        )}
        {canRevoked && (
          <Button
            variant="contained"
            onClick={() => setIsRevokedCommentModalVisible(true)}
          >
            {t("Revoke")}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

FireSafetyBuildingApprovalActionButton.propTypes = {
  applicationData: PropTypes.object,
  isBuildingNameSelected: PropTypes.bool,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};
export default withTranslation()(FireSafetyBuildingApprovalActionButton);
